<template>
  <div
    v-if="isSet()"
    :class="$style.prices"
  >
    <!-- Header -->
    <div class="mt-group">
      <p v-html="priceDescription" />
    </div>

    <!-- Family -->
    <section class="mt-section">
      <div class="mt-group mt-grid in10">
        <div class="row">
          <div class="col is5 is10-xm is10-s is10-xs">
            <div class="mt-line mt-grid in5">
              <div class="row">
                <div class="col is3">
                  <span class="mt-label mt-label--black mt-label--capitalise mt-label--size12">{{
                    $t('pages.submissions.components.prices.family.heading')
                  }}</span>
                </div>
                <div class="col is2">
                  <span class="mt-label mt-label--black mt-label--capitalise mt-label--size12">
                    {{ `${currencies.options[0].acronym} ${$t('pages.submissions.components.prices.price')}` }}
                  </span>
                  <span class="mt-required" />
                </div>
              </div>
            </div>

            <div :class="[$style.priceGroup, $style.priceGroupShadowLeft]">
              <div :class="$style.priceRow">
                <div
                  class="mt-grid in5"
                  :class="$style.priceRowInside"
                >
                  <div class="row">
                    <div class="col is3">
                      <span :class="[$style.priceFont, $style.priceFontThumb]">
                        <fou-picture
                          :id="$vId(this, 'family-package-render')"
                          :src="packages.familyPackage.renderSrc"
                          :alt="packages.familyPackage.name"
                        />
                      </span>
                    </div>
                    <div class="col is2">
                      <div :class="$style.currencyItem">
                        <div
                          class="mt-input"
                          :class="[
                            $style.currency,
                            $style.currencyMandatory,
                            { 'mt-input--error': Object.hasOwn(errors, 'family_currency 0 0 prices') }
                          ]"
                          :data-currency-symbol="currencies.options[0].symbol"
                        >
                          <TextField
                            :id="$vId(this, 'family_currency 0 0')"
                            v-model="packages.familyPackage.prices[0].price"
                            name="family_currency 0 0 prices"
                            type="text"
                            :label="`${currencies.options[0].symbol} price`"
                            :show-error-message="false"
                            :input-class="$style.currencyInput"
                            :rules="`fou_numbers|required|max_decimal:10000|min_limit:5,${submission.isLive}`"
                            :placeholder="getConvertedAmount(currencies.options[0].acronym, '')"
                            :blur="(key, idx) => savePrices(key, idx, 'family_currency 0 0 prices')"
                            :focus="editPrices(packages.familyPackage)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col is5 is10-xm is10-s is10-xs">
            <div class="mt-line">
              <fou-slider
                :id="$vId(this, 'family-price-header')"
                slave
              >
                <ul :class="$style.currencies">
                  <template v-for="(currency, i) in currencies.options">
                    <li
                      v-if="i !== 0"
                      :key="currency"
                    >
                      <span
                        :class="$style.currencyItem"
                        class="mt-label mt-label--capitalise mt-label--size12"
                      >{{
                        `${currency.acronym} ${$t('pages.submissions.components.prices.price')}`
                      }}</span>
                    </li>
                  </template>
                </ul>
              </fou-slider>
            </div>
            <div
              v-if="currencies.options.length"
              :class="[$style.priceGroup, $style.priceGroupShadowLeft]"
            >
              <div :class="$style.priceRow">
                <fou-slider
                  :id="$vId(this, 'family-price-content')"
                  :slaves="[$vId(this, 'family-price-header')]"
                >
                  <ul :class="[$style.currencies, $style.priceRowInsideNoBorder]">
                    <template v-for="(currency, i) in currencies.options">
                      <li
                        v-if="i !== 0"
                        :key="currency"
                        :class="$style.currencyItem"
                      >
                        <span
                          class="mt-input"
                          :data-currency-symbol="currency.symbol"
                          :class="[
                            $style.currency,
                            { 'mt-input--error': Object.hasOwn(errors, `family_currency 0 ${i} prices`) }
                          ]"
                        >
                          <TextField
                            :id="$vId(this, `family_currency 0 ${i}`)"
                            v-model="packages.familyPackage.prices[i].price"
                            :name="`family_currency 0 ${i} prices`"
                            type="text"
                            :label="`family pack price ${currency.symbol}`"
                            :show-error-message="false"
                            :input-class="$style.currencyInput"
                            :rules="`fou_numbers|max_decimal:999999|family_price_rule:${getSingleStylePriceList(
                              packages,
                              currency.acronym
                            )}`"
                            :placeholder="getConvertedAmount(currency.acronym, packages.familyPackage.prices[0].price)"
                            :blur="(key, idx) => savePrices(key, idx, `family_currency 0 ${i} prices`)"
                            :focus="editPrices(packages.familyPackage)"
                          />
                        </span>
                      </li>
                      <!--  -->
                    </template>
                  </ul>
                </fou-slider>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isWarning(packages.familyPackage.prices[0].price)"
        class="mt-warn mt-20"
        v-html="$t('pages.submissions.components.prices.warning')"
      />
      <div v-if="familyCurrencyError.length > 0">
        <p
          v-for="error in familyCurrencyError"
          :key="error"
          class="mt-error"
        >
          {{ error }}
        </p>
      </div>
    </section>

    <!-- Styles -->
    <section
      v-if="packages.singleStyles.length > 0"
      class="mt-section"
    >
      <div class="mt-group mt-grid in10">
        <div class="row">
          <div class="col is5 is10-xm is10-s is10-xs">
            <div class="mt-line mt-grid in5">
              <div class="row">
                <div class="col is3">
                  <span class="mt-label mt-label--black mt-label--capitalise mt-label--size12">{{
                    $t('pages.submissions.components.prices.styles.heading')
                  }}</span>
                </div>
                <div class="col is2">
                  <span class="mt-label mt-label--black mt-label--capitalise mt-label--size12">
                    {{ `${currencies.options[0].acronym} ${$t('pages.submissions.components.prices.price')}` }}
                  </span>
                  <span class="mt-required" />
                </div>
              </div>
            </div>
            <div
              class="mt-grid in5"
              :class="$style.priceGroup"
            >
              <div
                v-for="(s, key) in packages.singleStyles"
                :key="key"
                class="row"
                :class="$style.priceRow"
              >
                <div class="col is5">
                  <div
                    class="mt-grid in5"
                    :class="$style.priceRowInside"
                  >
                    <div class="row">
                      <div class="col is3">
                        <span :class="[$style.priceFont, $style.priceFontThumb]">
                          <fou-picture
                            :id="$vId(this, `style-package-render${key}`)"
                            :src="s.renderSrc"
                            :alt="s.name"
                          />
                        </span>
                      </div>
                      <div class="col is2">
                        <div :class="$style.currencyItem">
                          <span
                            class="mt-input"
                            :class="[
                              $style.currency,
                              $style.currencyMandatory,
                              { 'mt-input--error': Object.hasOwn(errors, `currency style ${key} 0 prices`) }
                            ]"
                            :data-currency-symbol="currencies.options[0].symbol"
                          >
                            <TextField
                              :id="$vId(this, `currency style ${key} 0`)"
                              v-model="s.prices[0].price"
                              :name="`currency style ${key} 0 prices`"
                              type="text"
                              :label="`${s.name} ${currencies.options[0].symbol} price`"
                              :show-error-message="false"
                              :input-class="$style.currencyInput"
                              :rules="`fou_numbers|required|max_decimal:10000|price_rule:${packages.familyPackage.prices[0].price},${submission.isLive}`"
                              :placeholder="getConvertedAmount(currencies.options[0].acronym, '')"
                              :blur="() => savePrices(key, 0, `currency style ${key} 0 prices`)"
                              :focus="() => editSingleStyle(key, 0)"
                            />
                          </span>
                          <transition name="fade">
                            <button
                              v-if="getSyncPriceState(key, 0)"
                              type="button"
                              :class="$style.currencySync"
                              @click.stop="syncPrice(key, 0)"
                            >
                              <fill-arrow-icon />
                            </button>
                          </transition>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col is5 is10-xm is10-s is10-xs">
            <div class="mt-line">
              <fou-slider
                :id="$vId(this, 'styles-price-header')"
                slave
              >
                <ul :class="$style.currencies">
                  <template v-for="(currency, i) in currencies.options">
                    <li
                      v-if="i !== 0"
                      :class="$style.currencyItem"
                    >
                      <span class="mt-label mt-label--capitalise mt-label--size12">{{
                        `${currency.acronym} ${$t('pages.submissions.components.prices.price')}`
                      }}</span>
                    </li>
                  </template>
                </ul>
              </fou-slider>
            </div>
            <div :class="[$style.priceGroup, $style.priceGroupShadowLeft]">
              <fou-slider
                :id="$vId(this, 'styles-price-content')"
                :slaves="[$vId(this, 'styles-price-header')]"
              >
                <div
                  v-for="(s, key) in packages.singleStyles"
                  :key="key"
                  :class="$style.priceRow"
                >
                  <ul :class="[$style.currencies, $style.priceRowInsideNoBorder]">
                    <template v-for="(currency, i) in currencies.options">
                      <li
                        v-if="i !== 0"
                        :class="$style.currencyItem"
                      >
                        <span
                          class="mt-input"
                          :data-currency-symbol="currency.symbol"
                          :class="[
                            $style.currency,
                            { 'mt-input--error': Object.hasOwn(errors, `currency style ${key} ${i} prices`) }
                          ]"
                        >
                          <input
                            :id="$vId(this, `currency style ${key} ${i}`)"
                            v-model="s.prices[i].price"
                            :name="`currency style ${key} ${i} prices`"
                            type="number"
                            :label="`${s.name} ${currency.symbol} price`"
                            :class="$style.currencyInput"
                            :placeholder="getConvertedAmount(currency.acronym, s.prices[0].price)"
                            @blur="(e) => validateAndSavePrices(e, key, i, currency.acronym, packages.familyPackage.prices[i].price, packages.familyPackage.prices[0].price)"
                            @focus="() => editSingleStyle(key, i)"
                          />
                        </span>
                        <transition name="fade">
                          <button
                            v-if="getSyncPriceState(key, i)"
                            type="button"
                            :class="$style.currencySync"
                            @click.stop="syncPrice(key, i)"
                          >
                            <fill-arrow-icon />
                          </button>
                        </transition>
                      </li>
                    </template>
                  </ul>
                </div>
              </fou-slider>
            </div>
          </div>
        </div>
        <div
          v-if="isWarning(packages.singleStyles, 'multiple')"
          class="mt-warn mt-20"
          v-html="$t('pages.submissions.components.prices.warning')"
        />
      </div>

      <fou-group :class="$style.notes">
        <fill-arrow-icon :class="$style.notesIcon" />
        <span class="mt-serif mt-serif--size4">{{ $t('pages.submissions.components.prices.styles.auto_fill') }}</span>
      </fou-group>

      <fou-group :key = "Object.keys(errors).length" v-if="getMergedErrors('currency style').length > 0">
        <p
          v-for="error in getMergedErrors('currency style')"
          class="mt-error"
        >
          {{ error }}
        </p>
      </fou-group>
    </section>

    <!-- Custom -->
    <section
      v-if="packages.customPackages.length > 0"
      class="mt-section"
    >
      <div
        v-for="(c, key) in packages.customPackages"
        :key="key"
        :class="$style.customSubpackage"
      >
        <!-- Controls -->
        <div :class="$style.customControls">
          <button
            :id="$vId(this, `remove-custom-${key}`)"
            type="button"
            :class="$style.customDeleteLink"
            @click="removeCustomPackage(key)"
          >
            <trash-icon />
          </button>
        </div>

        <!-- Prices -->
        <fou-group>
          <div class="mt-group mt-grid in10">
            <div class="row">
              <!-- Left Column -->
              <div class="col is5">
                <div class="mt-line mt-grid in5">
                  <div
                    class="row"
                    :class="$style.paddingLeft15"
                  >
                    <div class="col is3">
                      <span class="mt-label mt-label--black mt-label--capitalise mt-label--size12">
                        {{ $t('pages.submissions.components.prices.custom.heading') }}
                      </span>
                    </div>
                    <div class="col is2">
                      <span
                        class="mt-label mt-label--black mt-label--capitalise mt-label--size12"
                        :class="$style.marginLeftMinus6"
                      >
                        {{ `${currencies.options[0].acronym} ${$t('pages.submissions.components.prices.price')}` }}
                      </span>
                      <span class="mt-required" />
                    </div>
                  </div>
                </div>
                <div :class="$style.priceGroup">
                  <div :class="$style.priceRow">
                    <div
                      class="mt-grid in5"
                      :class="$style.priceRowInside"
                    >
                      <div class="row">
                        <div class="col is3" :class="{ [$style.bannedworderrorclass]: bannedWordForSubpackage[key] || warningWordForSubpackage[key]}">
                          <TextField
                            :id="$vId(this, `currency custom${key}.custom package name${key}`)"
                            v-model="c.name"
                            :name="`currency custom${key}.custom package name${key} prices`"
                            type="text"
                            :errorClass="$style.errorField"
                            :label="`Sub family name ${key + 1}`"
                            :show-error-message="false"
                            :input-class="[
                              $style.priceFont,
                              {
                                'mt-input--error': Object.hasOwn(
                                  errors,
                                  `currency custom${key}.custom package name${key} prices`
                                )
                              }
                            ]"
                            :moduleName="this.$route.name"
                            :foundryId="this.submission.foundry?.foundryId"
                            :description="c.name"
                            @update-banned-word="bannedWordForSubpackage[key] = $event"
                            @update-warning-existence="warningWordForSubpackage[key] = $event"
                            @update-warning-words="warningWordListForSubpackage[key] = $event"
                            :rules="`max:100|required|script_tag_validate|unique:${getPackageNames()}`"
                            :placeholder="$t('pages.submissions.components.prices.custom.name.placeholder')"
                            :blur="() => updateSubPackageName(c.name, key)"
                          />
                        </div>
                        <div class="col is2">
                          <div :class="$style.currencyItem">
                            <div
                              class="mt-input"
                              :class="[
                                $style.currency,
                                $style.currencyMandatory,
                                {
                                  'mt-input--error': Object.hasOwn(errors, `currency custom${key}.currency 0 0 prices`)
                                }
                              ]"
                              :data-currency-symbol="currencies.options[0].symbol"
                            >
                              <TextField
                                v-if="c?.prices?.[0]"
                                :id="$vId(this, `currency custom${key}.currency 0 0`)"
                                v-model="c.prices[0].price"
                                :name="`currency custom${key}.currency 0 0 prices`"
                                type="text"
                                :label="`${key + 1} ${currencies.options[0].symbol} price`"
                                :show-error-message="false"
                                :input-class="[$style.currencyInput]"
                                :rules="`fou_numbers|required|max_decimal:10000|price_rule:${packages.familyPackage.prices[0].price},${submission.isLive}`"
                                :placeholder="getConvertedAmount(currencies.options[0].acronym, '')"
                                :blur="() => {saveCustomPrices(key, 0, `currency custom${key}.currency 0 0 prices`, c.prices[0].price)}"
                                :focus="() => editPrices(c)"
                              />
                            </div>
                          </div>
                        </div>
                        
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- # End Left Column -->

              <!-- Right Column -->
              <div class="col is5">
                <div class="mt-line">
                  <fou-slider
                    :id="$vId(this, `custom-price-header-${key}`)"
                    slave
                  >
                    <ul :class="$style.currencies">
                      <template v-for="(currency, i) in currencies.options">
                        <li
                          v-if="i !== 0"
                          :class="$style.currencyItem"
                        >
                          <span class="mt-label mt-label--capitalise mt-label--size12">
                            {{ `${currency.acronym} ${$t('pages.submissions.components.prices.price')}` }}
                          </span>
                        </li>
                      </template>
                    </ul>
                  </fou-slider>
                </div>
                <div :class="[$style.priceGroup, $style.priceGroupShadowLeft]">
                  <div :class="$style.priceRow">
                    <fou-slider
                      :id="$vId(this, 'custom-price-content')"
                      :slaves="[$vId(this, `custom-price-header-${key}`)]"
                    >
                      <ul :class="[$style.currencies, $style.priceRowInsideNoBorder]">
                        <template v-for="(currency, i) in currencies.options">
                          <li
                            v-if="i > 0"
                            :class="$style.currencyItem"
                          >
                            <span
                              class="mt-input"
                              :data-currency-symbol="currency.symbol"
                              :class="[
                                $style.currency,
                                {
                                  'mt-input--error': Object.hasOwn(
                                    errors,
                                    `currency custom${key}.currency ${key} ${i} prices`
                                  )
                                }
                              ]"
                            >
                              <TextField
                                v-if="c.prices[i]"
                                :id="$vId(this, `currency custom${key}.currency ${key} ${i}`)"
                                v-model="c.prices[i].price"
                                :name="`currency custom${key}.currency ${key} ${i} prices`"
                                type="text"
                                :label="`${key + 1} ${currency.symbol} price`"
                                :show-error-message="false"
                                :input-class="[$style.currencyInput]"
                                :rules="`fou_numbers|max_decimal:999999|price_rule:${validateFamilySinglePack(
                                  currency.acronym,
                                  packages.familyPackage.prices[i].price,
                                  packages.familyPackage.prices[0].price
                                )}`"
                                :placeholder="getConvertedAmount(currency.acronym, c.prices[0].price)"
                                :blur="
                                  () => saveCustomPrices(key, 0, `currency custom${key}.currency ${key} ${i} prices`)
                                "
                                :focus="editPrices(c)"
                              />
                            </span>
                          </li>
                        </template>
                      </ul>
                    </fou-slider>
                  </div>
                </div>
              </div>
              <!-- # End Right Column -->
            </div>
            <div
              v-if="c.prices[0] && isWarning(c.prices[0].price)"
              class="mt-warn mt-20"
              v-html="$t('pages.submissions.components.prices.warning')"
            />
            <div class="row">
              <div
                class="col is5"
                :class="$style.marginLeft15"
              >
                <externalsku
                  v-if="submissionPackages.length > 0"
                  :id="$vId(this)"
                  :result="c"
                  :index="key"
                  tag="prices"
                  :element-id="key"
                  custom-pack-sku
                />
              </div>
            </div>
          </div>

          <!-- Price Errors -->
          <fou-group
            v-if="customSubPackageErrors(key).length > 0"
            :class="$style.marginLeft15"
          >
            <p
              v-for="error in customSubPackageErrors(key)"
              class="mt-error"
            >
              {{ error }}
            </p>
          </fou-group>
          <p v-if="bannedWordForSubpackage[key]" class="mt-error bannedword-error" :class="[$style.marginLeft15 , customSubPackageErrors(key).length>0 ? $style.marginTop : '']" >
            {{$t('pages.profile.foundry_profile.banned_word_error_message')}}
          </p>
          <p v-if="warningWordForSubpackage[key]" class="mt-error" :class="[$style.marginLeft15, $style.warningwordError, customSubPackageErrors(key).length>0 ? $style.marginTop : '']">
            {{ $t('pages.profile.foundry_profile.warning_word_error_message', {words: warningWordListForSubpackage[key].join('", "')}) }}
          </p>
          <!-- # Options -->
          <div
            class="mt-group mt-grid in10"
            :class="$style.marginLeft15"
          >
            <div class="row mt-line">
              <div :class="$style.dropdownWrapper">
                <span class="mt-label">{{ $t('pages.submissions.components.prices.custom.styles.label') }}</span>

                <vee-field
                  v-if="c?.select"
                  v-slot="{ field, errorMessage }"
                  :name="`font sub family${key} prices`"
                  :model-value="c.select.values"
                  rules="required|not_empty"
                  :label="`Select fonts (sub family ${key + 1})`"
                >
                  <div :class="$style.fontsWarning">
                    <fou-select
                      multiple
                      tags-outside
                      deselect-from-dropdown
                      :name="`font sub family${key} prices`"
                      :id="$vId(this, `font sub family${key}`)"
                      :input-class="[inputFont, inputWidth]"
                      :error-class="[errorCSS]"
                      :options="c.select.options"
                      :modelValue="c.select.values"
                      :reduce="op => op"
                      :close-on-select="false"
                      :is-error="Object.hasOwn(errors, `font sub family${key} prices`)"
                      :placeholder="$t('pages.submissions.components.prices.custom.styles.placeholder')"
                      @option:selected="
                        option => {
                          c.select.updateCustom(option), validateFontSelection(c.select, key) 
                        }
                      "
                      @option:deselected="option => optionDeselected(option, c.select, key)"
                      v-bind="field"
                      type="checkbox"
                      class-name="mt-checkbox"
                      style="width: 18rem;"
                    />
                    <div v-if="uploadFonts === false" :class="$style.submissionWarning">
                      <Loader :customStyle = true />
                      <p :class="$style.submissionWarningText">Submission in progress</p>
                    </div>
                  </div>
                  <p class="ml-0 mt-error">{{ errorMessage }}</p>
                </vee-field>
              </div>
            </div>
            <div class="row mt-group">
              <div class="col is10">
                <fou-select-tags
                  :id="$vId(this, `select-style-${key}-tags`)"
                  :fou-select="c.select"
                  :fou-select-id="$vId(this, `select-style-${key}`)"
                  @on:remove="option => optionDeselected(option, c.select, key)"
                />
              </div>
            </div>

            <div class="row mt-line">
              <div
                v-if="c.select?.values?.length === 1 || (c.select?.values?.length > c.select?.options?.length - 1)"
                class="col is10"
              >
                <span class="mt-error">
                  {{validateSelectFontsLength(c.select, key)}}
                </span>
              </div>
            </div>
          </div>
          <!-- # End Options -->
        </fou-group>
        <!-- # End Prices -->
      </div>
      <div v-if="subPackageDoublesError">
        <p
          v-for="error in subPackageDoublesError"
          class="mt-error"
        >
          {{ error }}
        </p>
      </div>
    </section>

    <!-- Custom create -->
    <section class="mt-section" v-if="submission.userMetaData.binaries.length > 2 && (submission.status === 'in_work' || submission.status === 'live' || submission.status === 'in_correction')">
      <div class="mt-group">
        <p v-html="validationMessage()"></p>
      </div>
      <button
        v-if="validatePackage(packages.customPackages.length)"
        :id="$vId(this, 'add-package')"
        type="button"
        class="mt-button mt-button--inverted mt-button--small"
        @click="addCustomPackage()">
        {{$t('pages.submissions.components.prices.custom.create')}}
      </button>
    </section>
  </div>
</template>

<script type="text/ecmascript-6" src="./script/index.js"></script>
<style lang="scss" src="./stylesheets/index.scss" module></style>
